import React, { useEffect, useState } from "react";
import axios from "axios";
import styles from './Myordertab.module.css';
import { FiCalendar, FiXCircle, FiTrash2 } from "react-icons/fi"; 
import config from '../../config/config';

const Myordertab = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notification, setNotification] = useState({ show: false, message: "", type: "" });
  const [isDeleting, setIsDeleting] = useState(false); 

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const userData = JSON.parse(localStorage.getItem("userData"));
      const userId = userData?._id;

      if (!userId) return;

      const res = await axios.get(`${config.apiBaseUrl}/api/user-orders/${userId}`);
      setOrders(res.data);
    } catch (err) {
      console.error("Error fetching orders:", err);
      showNotification("Failed to load orders", "error");
    } finally {
      setLoading(false);
    }
  };
  const showNotification = (message, type) => {
    setNotification({ show: true, message, type });
    setTimeout(() => {
      setNotification({ show: false, message: "", type: "" });
    }, 3000);
  };

  const canCancelOrder = (orderDate) => {
    if (!orderDate) return false;
    const orderTime = new Date(orderDate).getTime();
    const currentTime = new Date().getTime();
    const thirtyMinutes = 30 * 60 * 1000;
    return (currentTime - orderTime) < thirtyMinutes;
  };

  const handleCancelOrder = async (orderId) => {
    try {
      await axios.put(`${config.apiBaseUrl}/api/user-orders/cancel/${orderId}`);
      setOrders(orders.map(order => 
        order._id === orderId ? { ...order, status: "cancelled" } : order
      ));
      showNotification("Order cancelled successfully", "success");
    } catch (err) {
      console.error("Error cancelling order:", err);
      showNotification("Failed to cancel order", "error");
    }
  };

  const getStatusBadge = (status) => {
    switch (status?.toLowerCase()) {
      case 'delivered':
        return <span className={`${styles.statusBadge} ${styles.statusDelivered}`}>{status}</span>;
      case 'accepted':
        return <span className={`${styles.statusBadge} ${styles.statusAccepted}`}>{status}</span>;
      case 'cancelled':
        return <span className={`${styles.statusBadge} ${styles.statusCancelled}`}>{status}</span>;
      default:
        return <span className={`${styles.statusBadge} ${styles.statusPending}`}>{status || 'pending'}</span>;
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    const options = { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    };
    return new Date(dateString).toLocaleDateString('en-US', options);
  };
const handleDeleteDeliveredCancelled = async () => {
  if (!window.confirm("Are you sure you want to delete all delivered and cancelled orders? This action cannot be undone.")) {
    return;
  }

  try {
    setIsDeleting(true);
    const response = await axios.delete(
      `${config.apiBaseUrl}/api/user-orders/delete/delivered-cancelled`
    );
    
    if (response.data.deletedCount > 0) {
      showNotification(
        `Deleted ${response.data.deletedCount} delivered/cancelled orders`, 
        "success"
      );
      // Filter out deleted orders from local state
      setOrders(orders.filter(
        order => !['delivered', 'cancelled'].includes(order.status.toLowerCase())
      ));
    } else {
      showNotification("No delivered or cancelled orders to delete", "info");
    }
  } catch (err) {
    console.error("Error deleting orders:", err);
    showNotification("Failed to delete orders", "error");
  } finally {
    setIsDeleting(false);
  }
};



  if (loading) {
    return (
      <div className={styles.container}>
        <h2 className={styles.heading}>My Orders</h2>
        <div className={styles.emptyMessage}>Loading your orders...</div>
      </div>
    );
  }


  return (
    
    <div className={styles.container}>
      <div className={styles.header}>
  <h2 className={styles.heading}>My Orders</h2>
  {orders.some(o => ['delivered', 'cancelled'].includes(o.status?.toLowerCase())) && (
    <button
      onClick={handleDeleteDeliveredCancelled}
      disabled={isDeleting}
      className={styles.deleteAllButton}
    >
      <FiTrash2 />
      {isDeleting ? 'Deleting...' : 'Delete Delivered & Cancelled'}
    </button>
  )}
</div>
      
      {/* Custom Notification */}
      {notification.show && (
        <div className={`${styles.notification} ${styles[notification.type]}`}>
          {notification.message}
        </div>
      )}

      {orders.length === 0 ? (
        <div className={styles.emptyMessage}>No orders found.</div>
      ) : (
        <div className={styles.orderList}>
          {orders.map((order, i) => (
            <div key={order._id} className={styles.orderCard}>
              <div className={styles.orderHeader}>
                <h3>Order #{i + 1}</h3>
                {order.status.toLowerCase() !== 'cancelled' && 
                 canCancelOrder(order.createdAt) && (
                  <button 
                    onClick={() => handleCancelOrder(order._id)}
                    className={styles.cancelButton}
                  >
                    <FiXCircle /> Cancel Order
                  </button>
                )}
              </div>
              <div className={styles.orderDate}>
                <FiCalendar />
                {formatDate(order.createdAt)}
                {!canCancelOrder(order.createdAt) && order.status.toLowerCase() !== 'cancelled' && (
                  <span className={styles.cancelExpired}>Cancellation expired</span>
                )}
              </div>
              <p><strong>Status:</strong> {getStatusBadge(order.status)}</p>
              <p><strong>Total:</strong> Rs. {order.totalAmount?.toFixed(2)}</p>
              <p><strong>Shipping:</strong> Rs. {order.shippingCost?.toFixed(2)}</p>
              
              <h4>Products:</h4>
              <ul>
                {order.products?.map((p, idx) => (
                  <li key={idx}>
                    {p.name} (Qty: {p.quantity}, Rs. {p.price?.toFixed(2)})
                  </li>
                ))}
              </ul>
              
              <h4>Shipping Address:</h4>
              <p>
                {order.shippingDetails?.address}, {order.shippingDetails?.postalCode}
              </p>
              <p><strong>Phone:</strong> {order.shippingDetails?.phone}</p>
              <p><strong>Email:</strong> {order.shippingDetails?.email}</p>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default Myordertab;